import { useState } from 'react';
import {
  type GestureResponderEvent,
  Pressable,
  type PressableProps,
  type StyleProp,
  View,
  type ViewStyle,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Icon from '@/components/Unistyles/Icon';

type CheckboxVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'info'
  | 'error';
type CheckboxSize = 'sm' | 'md' | 'lg';

interface CheckboxProps
  extends Omit<
    PressableProps,
    | 'accessibilityRole'
    | 'accessibilityState'
    | 'children'
    | 'disabled'
    | 'style'
  > {
  children?: React.ReactNode;
  defaultValue?: boolean;
  disabled?: boolean;
  onValueChange?: (value: boolean) => void;
  size?: CheckboxSize;
  showCheckmark?: boolean;
  style?: StyleProp<ViewStyle>;
  value?: boolean;
  variant?: CheckboxVariant;
}

export default function Checkbox({
  children,
  defaultValue = false,
  disabled,
  onValueChange,
  size = 'md',
  showCheckmark = true,
  style: extraStyles,
  value,
  variant = 'primary',
  ...props
}: CheckboxProps) {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const checked = value ?? uncontrolledValue;

  styles.useVariants({
    checked,
    disabled,
    size,
    variant,
  });

  const onPress = (event?: GestureResponderEvent) => {
    event?.stopPropagation();

    if (disabled) {
      return;
    }

    const nextValue = !checked;

    if (value === undefined) {
      setUncontrolledValue(nextValue);
    }

    onValueChange?.(nextValue);
  };

  return (
    <Pressable
      {...props}
      accessibilityRole="checkbox"
      accessibilityState={{ checked, disabled }}
      aria-checked={checked}
      disabled={disabled}
      onPress={onPress}
      style={[styles.root, extraStyles]}
    >
      <View pointerEvents="none" style={styles.indicator}>
        {checked && showCheckmark ? (
          <Icon name="checkmark" size={getIconSize(size)} variant="white" />
        ) : null}
      </View>
      {children}
    </Pressable>
  );
}

function getIconSize(size: CheckboxSize) {
  switch (size) {
    case 'lg':
      return 'xl';
    case 'sm':
      return 'md';
    default:
      return 'lg';
  }
}

const styles = StyleSheet.create((theme) => ({
  indicator: {
    alignItems: 'center',
    borderColor: theme.colors.borderMuted,
    borderRadius: theme.borderRadius.lg,
    borderWidth: theme.size(0.5),
    justifyContent: 'center',
    variants: {
      checked: {
        true: {},
      },
      disabled: {
        true: {},
      },
      size: {
        lg: {
          height: theme.size(7),
          width: theme.size(7),
        },
        md: {
          height: theme.size(6),
          width: theme.size(6),
        },
        sm: {
          height: theme.size(4),
          width: theme.size(4),
        },
      },
      variant: {
        error: {},
        info: {},
        primary: {},
        secondary: {},
        success: {},
        warning: {},
      },
    },
    compoundVariants: [
      {
        checked: true,
        variant: 'error',
        styles: {
          backgroundColor: theme.colors.error,
          borderColor: theme.colors.error,
        },
      },
      {
        checked: true,
        variant: 'info',
        styles: {
          backgroundColor: theme.colors.info,
          borderColor: theme.colors.info,
        },
      },
      {
        checked: true,
        variant: 'primary',
        styles: {
          backgroundColor: theme.colors.primary,
          borderColor: theme.colors.primary,
        },
      },
      {
        checked: true,
        variant: 'secondary',
        styles: {
          backgroundColor: theme.colors.secondary,
          borderColor: theme.colors.secondary,
        },
      },
      {
        checked: true,
        variant: 'success',
        styles: {
          backgroundColor: theme.colors.success,
          borderColor: theme.colors.success,
        },
      },
      {
        checked: true,
        variant: 'warning',
        styles: {
          backgroundColor: theme.colors.warning,
          borderColor: theme.colors.warning,
        },
      },
    ],
  },
  root: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.space('sm'),
    variants: {
      checked: {
        true: {},
      },
      disabled: {
        true: {
          opacity: 0.5,
        },
      },
      size: {
        lg: {},
        md: {},
        sm: {},
      },
      variant: {
        error: {},
        info: {},
        primary: {},
        secondary: {},
        success: {},
        warning: {},
      },
    },
  },
}));
